import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Trash2, UserPlus } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

interface PreviewViewer {
  user_id: string;
  label: string | null;
  email: string | null;
  phone: string | null;
  full_name: string | null;
  created_at: string;
}

const ADD_ERRORS: Record<string, string> = {
  user_not_found: 'No H-Plate account uses that email or phone. They need to sign up in the app first.',
  identifier_required: 'Enter an email or phone number.',
  not_admin: 'Only platform admins can change the preview team.',
};

/**
 * Manage the accounts that can see "Team preview only" campaigns in the
 * app. Platform admins are always included.
 */
export default function PreviewTeamDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [viewers, setViewers] = useState<PreviewViewer[]>([]);
  const [loading, setLoading] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const [label, setLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc('admin_list_ad_preview_viewers');
    setLoading(false);
    if (error) {
      toast.error(error.message || 'Could not load the preview team');
      return;
    }
    setViewers((data as PreviewViewer[]) ?? []);
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const add = async () => {
    if (!identifier.trim()) return;
    setAdding(true);
    const { error } = await supabase.rpc('admin_add_ad_preview_viewer', {
      p_identifier: identifier.trim(),
      p_label: label.trim() || null,
    });
    setAdding(false);
    if (error) {
      const key = Object.keys(ADD_ERRORS).find((k) => error.message?.includes(k));
      toast.error(key ? ADD_ERRORS[key] : error.message);
      return;
    }
    toast.success('Added to the preview team. They see samples after restarting the app.');
    setIdentifier('');
    setLabel('');
    void load();
  };

  const remove = async (v: PreviewViewer) => {
    setRemovingId(v.user_id);
    const { error } = await supabase.rpc('admin_remove_ad_preview_viewer', {
      p_user_id: v.user_id,
    });
    setRemovingId(null);
    if (error) {
      toast.error(error.message || 'Could not remove');
      return;
    }
    setViewers((prev) => prev.filter((x) => x.user_id !== v.user_id));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle>Preview team</DialogTitle>
          <DialogDescription className="text-gray-400">
            These accounts see campaigns marked Team preview in the H-Plate app.
            Riders never do. Admin accounts are always included.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-400 py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading
            </div>
          ) : viewers.length === 0 ? (
            <p className="text-sm text-gray-500 py-2">No one yet.</p>
          ) : (
            viewers.map((v) => (
              <div
                key={v.user_id}
                className="flex items-center gap-3 rounded-lg border border-gray-800 px-3 py-2"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">
                    {v.label || v.full_name || v.email || v.phone || v.user_id}
                  </p>
                  <p className="text-[11px] text-gray-500 truncate">
                    {[v.full_name, v.email, v.phone].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-gray-400 hover:text-red-400 w-8 h-8"
                  onClick={() => void remove(v)}
                  disabled={removingId === v.user_id}
                  aria-label={`Remove ${v.label || v.email || 'viewer'}`}
                >
                  {removingId === v.user_id
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Trash2 className="w-3.5 h-3.5" />}
                </Button>
              </div>
            ))
          )}
        </div>

        <div className="border-t border-gray-800 pt-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-400">Email or phone of their app account</Label>
              <Input
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="jay@example.com or 767 555 0100"
                className="bg-gray-950 border-gray-800 text-white"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-400">Name (optional)</Label>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Jay"
                className="bg-gray-950 border-gray-800 text-white"
              />
            </div>
          </div>
          <Button
            onClick={() => void add()}
            disabled={adding || !identifier.trim()}
            className="bg-violet-600 hover:bg-violet-500 text-white"
          >
            {adding
              ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
              : <UserPlus className="w-4 h-4 mr-2" />}
            Add to preview team
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
